import { useEffect, useState } from "react"
import { Form, Row, Col } from "antd"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { modelSelector } from "store/selectors"
import AddStaffModal from "pagesV2/pro/Staff/AddStaffModal"
import { useResourceData } from "pagesV2/pro/Experiences/NewListingResources/hooks/useResourceData"
import { useEquipmentManagement } from "pagesV2/pro/Experiences/NewListingResources/hooks/useEquipmentManagement"
import { useCrewMemberManagement } from "pagesV2/pro/Experiences/NewListingResources/hooks/useCrewMemberManagement"
import { EquipmentSection } from "pagesV2/pro/Experiences/NewListingResources/components/EquipmentSection"
import { CrewMemberSection } from "pagesV2/pro/Experiences/NewListingResources/components/CrewMemberSection"
import { buildResourcesPayload } from "../mappers/toApiPayload"
import { useWizardContext } from "../WizardContext"

/**
 * Resources step — equipment and crew member assignment.
 *
 * No AntD form fields used here; all state is local and registered
 * via stepSaveHandlersRef so useExperienceWizard can trigger the save.
 */
export const ResourcesStep = () => {
  const { t } = useTranslation()
  const { stepSaveHandlersRef, stepValidateHandlersRef } = useWizardContext()
  const trip = useSelector(modelSelector("trip"))

  const [autoAssignEquipment, setAutoAssignEquipment] = useState(false)
  const [equipmentError, setEquipmentError] = useState(false)
  const { equipmentData, crewMemberData, categoryOptions, roleOptions } = useResourceData()

  const {
    selectedEquipment,
    setSelectedEquipment,
    selectedCategories,
    setSelectedCategories,
    selectedEquipmentData,
    handleEditEquipment,
    handleRemoveEquipment,
    handleOpenModal
  } = useEquipmentManagement(equipmentData)

  const {
    selectedCrewMembers,
    setSelectedCrewMembers,
    selectedCrewMembersData,
    selectedRoles,
    setSelectedRoles,
    isAddStaffModalVisible,
    editingCrewMember,
    handleEditCrewMember,
    handleRemoveCrewMember,
    handleOpenStaffModal,
    handleCloseStaffModal
  } = useCrewMemberManagement(crewMemberData)

  // Initialize from trip server state
  useEffect(() => {
    if (trip?.boat_ids) setSelectedEquipment(trip.boat_ids.map(String))
    if (trip?.assigned_staff_ids) setSelectedCrewMembers(trip.assigned_staff_ids.map(String))
    if (trip?.auto_assign_equipment !== undefined) setAutoAssignEquipment(trip.auto_assign_equipment)
  }, [trip, setSelectedEquipment, setSelectedCrewMembers])

  // Listen for validation failure triggered by tab switch save handler
  useEffect(() => {
    const handleValidationFailed = () => setEquipmentError(true)
    window.addEventListener("resourcesEquipmentValidationFailed", handleValidationFailed)
    return () => window.removeEventListener("resourcesEquipmentValidationFailed", handleValidationFailed)
  }, [])

  const handleEquipmentChange = (value) => {
    setSelectedEquipment(value)
    if (value?.filter((v) => v !== "all").length > 0) setEquipmentError(false)
  }

  const handleAutoAssignChange = (value) => {
    setAutoAssignEquipment(value)
    if (!value) setEquipmentError(false)
  }

  // Register validate handler — blocks navigation and shows card error + modal
  useEffect(() => {
    const handlers = stepValidateHandlersRef.current
    handlers["resources"] = async () => {
      const validEquipment = selectedEquipment.filter((v) => v !== "all")
      if (autoAssignEquipment && validEquipment.length === 0) {
        window.dispatchEvent(new CustomEvent("resourcesEquipmentValidationFailed"))
        const err = new Error("equipment_required")
        err.errorFields = [
          {
            errors: [
              t("listings.tabs.resources.equipment_required_for_toggle", {
                defaultValue: "At least one equipment must be selected when 'Booking requires equipment' is enabled."
              })
            ]
          }
        ]
        throw err
      }
    }
    return () => {
      delete handlers["resources"]
    }
  }, [stepValidateHandlersRef, selectedEquipment, autoAssignEquipment, t])

  // Register save handler
  useEffect(() => {
    stepSaveHandlersRef.current["resources"] = async () => {
      return buildResourcesPayload({ selectedEquipment, selectedCrewMembers, autoAssignEquipment })
    }
    return () => {
      delete stepSaveHandlersRef.current["resources"]
    }
  }, [stepSaveHandlersRef, selectedEquipment, selectedCrewMembers, autoAssignEquipment])

  return (
    <Form component={false}>
      <div className="px-xs">
        <Row gutter={[16, 16]} className="mt-4">
          <Col lg={12}>
            <EquipmentSection
              equipmentData={equipmentData}
              selectedEquipment={selectedEquipment}
              onEquipmentChange={handleEquipmentChange}
              selectedCategories={selectedCategories}
              onCategoryChange={setSelectedCategories}
              categoryOptions={categoryOptions}
              selectedEquipmentData={selectedEquipmentData}
              onEdit={handleEditEquipment}
              onRemove={handleRemoveEquipment}
              onAddClick={handleOpenModal}
              autoAssignEquipment={autoAssignEquipment}
              onAutoAssignChange={handleAutoAssignChange}
              hasError={equipmentError}
            />
          </Col>
          <Col lg={12}>
            <CrewMemberSection
              crewMemberData={crewMemberData}
              selectedCrewMembers={selectedCrewMembers}
              onCrewMemberChange={setSelectedCrewMembers}
              selectedRoles={selectedRoles}
              onRoleChange={setSelectedRoles}
              roleOptions={roleOptions}
              selectedCrewMembersData={selectedCrewMembersData}
              onEdit={handleEditCrewMember}
              onRemove={handleRemoveCrewMember}
              onAddClick={handleOpenStaffModal}
            />
          </Col>
        </Row>
      </div>
      <AddStaffModal
        staffId={editingCrewMember?.id}
        editMode={!!editingCrewMember?.id}
        open={isAddStaffModalVisible}
        onCancel={handleCloseStaffModal}
        memberType="crew"
      />
    </Form>
  )
}
